import type { EasingFunction, AnimationHandle } from './animate';
import { easing } from './animate';

export type MIAnimationHandle = AnimationHandle;

export interface MICameraKeyframe {
  position: [number, number, number];
  heading: number;
  tilt: number;
  roll?: number;
  fov?: number;
  time: number;
  easing?: EasingFunction;
}

export interface MIKeyframeAnimationOptions {
  loop?: boolean;
  pingPong?: boolean;
  speed?: number;
  onProgress?: (time: number, fraction: number, keyframeIndex: number) => void;
  onComplete?: () => void;
  onKeyframe?: (index: number, keyframe: MICameraKeyframe) => void;
}

export interface MIAlong3DOptions {
  duration: number;
  speed?: number;
  repeat?: boolean;
  easing?: EasingFunction;
  heightReference?: string;
  orientToPath?: boolean;
  tiltToPath?: boolean;
  rollFactor?: number;
  altitude?: number;
  onProgress?: (fraction: number, position: [number, number, number], heading: number) => void;
  onComplete?: () => void;
}

export interface MISceneClockOptions {
  startTime?: Date;
  endTime?: Date;
  currentTime?: Date;
  multiplier?: number;
  clockRange?: 'unbounded' | 'clamp' | 'loop';
  shouldAnimate?: boolean;
  clockStep?: 'system' | 'tick-dependent';
}

export class MISceneClock {
  public currentTime: Date;
  public startTime: Date;
  public endTime: Date;
  public multiplier: number;
  public clockRange: 'unbounded' | 'clamp' | 'loop';
  public shouldAnimate: boolean;
  public onTick: ((currentTime: Date) => void) | null;

  private _animationFrame: number | null;
  private _lastTickTime: number;

  constructor(options?: MISceneClockOptions) {
    this.startTime = options?.startTime || new Date();
    this.endTime = options?.endTime || new Date(Date.now() + 86400000);
    this.currentTime = options?.currentTime || new Date(this.startTime);
    this.multiplier = options?.multiplier || 1;
    this.clockRange = options?.clockRange || 'unbounded';
    this.shouldAnimate = options?.shouldAnimate || false;
    this.onTick = null;
    this._animationFrame = null;
    this._lastTickTime = 0;
  }

  start(): void {
    this.shouldAnimate = true;
    this._lastTickTime = performance.now();
    this._scheduleNextTick();
  }

  stop(): void {
    this.shouldAnimate = false;
    if (this._animationFrame !== null) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = null;
    }
    this.currentTime = new Date(this.startTime);
    this._lastTickTime = 0;
  }

  pause(): void {
    this.shouldAnimate = false;
    if (this._animationFrame !== null) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = null;
    }
  }

  resume(): void {
    if (!this.shouldAnimate) {
      this.shouldAnimate = true;
      this._lastTickTime = performance.now();
      this._scheduleNextTick();
    }
  }

  private _scheduleNextTick(): void {
    if (this._animationFrame !== null) {
      cancelAnimationFrame(this._animationFrame);
    }
    this._animationFrame = requestAnimationFrame((now) => this._tick(now));
  }

  private _tick(now: number): void {
    if (!this.shouldAnimate) {
      this._animationFrame = null;
      return;
    }

    const elapsedReal = now - this._lastTickTime;
    this._lastTickTime = now;

    const elapsedSim = elapsedReal * this.multiplier;
    const newTime = this.currentTime.getTime() + elapsedSim;

    const startMs = this.startTime.getTime();
    const endMs = this.endTime.getTime();

    switch (this.clockRange) {
      case 'clamp':
        if (this.multiplier >= 0) {
          this.currentTime = new Date(Math.min(newTime, endMs));
          if (newTime >= endMs) {
            this.shouldAnimate = false;
            this.onTick?.(this.currentTime);
            this._animationFrame = null;
            return;
          }
        } else {
          this.currentTime = new Date(Math.max(newTime, startMs));
          if (newTime <= startMs) {
            this.shouldAnimate = false;
            this.onTick?.(this.currentTime);
            this._animationFrame = null;
            return;
          }
        }
        break;

      case 'loop': {
        const range = endMs - startMs;
        if (range > 0) {
          let t = newTime - startMs;
          t = ((t % range) + range) % range;
          this.currentTime = new Date(startMs + t);
        } else {
          this.currentTime = new Date(newTime);
        }
        break;
      }

      case 'unbounded':
      default:
        this.currentTime = new Date(newTime);
        break;
    }

    this.onTick?.(this.currentTime);

    this._animationFrame = requestAnimationFrame((n) => this._tick(n));
  }
}

export interface MITimeSliderOptions {
  clock: MISceneClock;
  position?: 'bottom' | 'top';
  showPlayPause?: boolean;
  showSpeedControl?: boolean;
  showDateLabel?: boolean;
  dateFormat?: string;
  thumbCount?: 1 | 2;
  stops?: Date[];
  tickInterval?: number;
}

export class MITimeSlider {
  private _clock: MISceneClock;
  private _options: MITimeSliderOptions;
  private _element: HTMLElement | null;
  private _tickCleanup: (() => void) | null;

  constructor(options: MITimeSliderOptions) {
    this._clock = options.clock;
    this._options = {
      position: 'bottom',
      showPlayPause: true,
      showSpeedControl: false,
      showDateLabel: true,
      dateFormat: 'yyyy-MM-dd HH:mm',
      thumbCount: 1,
      ...options,
    };
    this._element = null;
    this._tickCleanup = null;
    this._buildElement();
  }

  private _formatDate(date: Date): string {
    const fmt = this._options.dateFormat || 'yyyy-MM-dd HH:mm';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return fmt
      .replace('yyyy', date.getFullYear().toString())
      .replace('MM', pad(date.getMonth() + 1))
      .replace('dd', pad(date.getDate()))
      .replace('HH', pad(date.getHours()))
      .replace('mm', pad(date.getMinutes()))
      .replace('ss', pad(date.getSeconds()));
  }

  private _buildElement(): void {
    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      left: 0; right: 0;
      ${this._options.position === 'top' ? 'top: 0;' : 'bottom: 0;'}
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: rgba(0, 0, 0, 0.7);
      color: #fff;
      font-family: sans-serif;
      font-size: 13px;
      z-index: 1000;
      user-select: none;
    `;

    if (this._options.showPlayPause) {
      const playBtn = document.createElement('button');
      playBtn.textContent = 'Play';
      playBtn.style.cssText = 'cursor:pointer; padding:4px 10px; border:1px solid #888; background:#333; color:#fff; border-radius:3px;';
      playBtn.addEventListener('click', () => {
        if (this._clock.shouldAnimate) {
          this._clock.pause();
          playBtn.textContent = 'Play';
        } else {
          this._clock.start();
          playBtn.textContent = 'Pause';
        }
      });
      container.appendChild(playBtn);
    }

    const startMs = this._clock.startTime.getTime();
    const endMs = this._clock.endTime.getTime();

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '1000';
    slider.value = '0';
    slider.style.cssText = 'flex:1; cursor:pointer;';
    slider.addEventListener('input', () => {
      const frac = parseInt(slider.value, 10) / 1000;
      this._clock.currentTime = new Date(startMs + frac * (endMs - startMs));
    });
    container.appendChild(slider);

    let dateLabel: HTMLSpanElement | null = null;
    if (this._options.showDateLabel) {
      dateLabel = document.createElement('span');
      dateLabel.style.cssText = 'min-width:140px; text-align:right; white-space:nowrap;';
      dateLabel.textContent = this._formatDate(this._clock.currentTime);
      container.appendChild(dateLabel);
    }

    if (this._options.showSpeedControl) {
      const speedSelect = document.createElement('select');
      speedSelect.style.cssText = 'padding:2px 4px; background:#333; color:#fff; border:1px solid #888; border-radius:3px;';
      [0.5, 1, 2, 5, 10, 50, 100].forEach((s) => {
        const opt = document.createElement('option');
        opt.value = s.toString();
        opt.textContent = `${s}x`;
        if (s === this._clock.multiplier) opt.selected = true;
        speedSelect.appendChild(opt);
      });
      speedSelect.addEventListener('change', () => {
        this._clock.multiplier = parseFloat(speedSelect.value);
      });
      container.appendChild(speedSelect);
    }

    const prevOnTick = this._clock.onTick;
    this._clock.onTick = (time: Date) => {
      prevOnTick?.(time);
      const range = endMs - startMs;
      if (range > 0) {
        const frac = (time.getTime() - startMs) / range;
        slider.value = Math.round(Math.max(0, Math.min(1, frac)) * 1000).toString();
      }
      if (dateLabel) {
        dateLabel.textContent = this._formatDate(time);
      }
    };
    this._tickCleanup = () => {
      this._clock.onTick = prevOnTick;
    };

    this._element = container;
  }

  getElement(): HTMLElement {
    if (!this._element) {
      this._buildElement();
    }
    return this._element!;
  }

  setVisible(visible: boolean): void {
    if (this._element) {
      this._element.style.display = visible ? 'flex' : 'none';
    }
  }

  destroy(): void {
    this._tickCleanup?.();
    this._tickCleanup = null;
    if (this._element && this._element.parentNode) {
      this._element.parentNode.removeChild(this._element);
    }
    this._element = null;
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return a + diff * t;
}

function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

function toDegrees(rad: number): number {
  return (rad * 180) / Math.PI;
}

function haversineDistance(
  lon1: number, lat1: number, alt1: number,
  lon2: number, lat2: number, alt2: number
): number {
  const R = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const groundDist = R * c;
  const dAlt = alt2 - alt1;
  return Math.sqrt(groundDist * groundDist + dAlt * dAlt);
}

function bearingBetween(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const dLon = toRadians(lon2 - lon1);
  const lat1R = toRadians(lat1);
  const lat2R = toRadians(lat2);
  const y = Math.sin(dLon) * Math.cos(lat2R);
  const x = Math.cos(lat1R) * Math.sin(lat2R) - Math.sin(lat1R) * Math.cos(lat2R) * Math.cos(dLon);
  return (toDegrees(Math.atan2(y, x)) + 360) % 360;
}

export const animate3D = {
  cameraKeyframes(
    scene: any,
    keyframes: MICameraKeyframe[],
    options?: MIKeyframeAnimationOptions
  ): MIAnimationHandle {
    if (!keyframes || keyframes.length === 0) {
      return {
        pause() {},
        resume() {},
        stop() {},
        isRunning() { return false; },
        getProgress() { return 0; },
      };
    }

    const sorted = [...keyframes].sort((a, b) => a.time - b.time);
    const totalDuration = sorted[sorted.length - 1].time;
    const speed = options?.speed ?? 1;
    const loop = options?.loop ?? false;
    const pingPong = options?.pingPong ?? false;

    let running = true;
    let paused = false;
    let progress = 0;
    let startTime = performance.now();
    let pauseTime = 0;
    let direction = 1;
    let lastKeyframeIndex = -1;

    function findKeyframeSegment(t: number): { index: number; a: MICameraKeyframe; b: MICameraKeyframe; localT: number } {
      if (t <= sorted[0].time) {
        return { index: 0, a: sorted[0], b: sorted[0], localT: 0 };
      }
      if (t >= sorted[sorted.length - 1].time) {
        const last = sorted[sorted.length - 1];
        return { index: sorted.length - 1, a: last, b: last, localT: 1 };
      }
      for (let i = 0; i < sorted.length - 1; i++) {
        if (t >= sorted[i].time && t <= sorted[i + 1].time) {
          const segDuration = sorted[i + 1].time - sorted[i].time;
          const localT = segDuration > 0 ? (t - sorted[i].time) / segDuration : 0;
          return { index: i, a: sorted[i], b: sorted[i + 1], localT };
        }
      }
      const last = sorted[sorted.length - 1];
      return { index: sorted.length - 1, a: last, b: last, localT: 1 };
    }

    function tick(now: number) {
      if (!running) return;
      if (paused) {
        requestAnimationFrame(tick);
        return;
      }

      const elapsed = (now - startTime) * speed;
      let currentTime: number;

      if (totalDuration <= 0) {
        currentTime = 0;
        progress = 1;
      } else {
        if (pingPong) {
          const cycle = elapsed / totalDuration;
          const cycleInt = Math.floor(cycle);
          const cycleFrac = cycle - cycleInt;
          direction = cycleInt % 2 === 0 ? 1 : -1;
          currentTime = direction === 1 ? cycleFrac * totalDuration : (1 - cycleFrac) * totalDuration;
          progress = cycleFrac;

          if (!loop && cycleInt >= 2) {
            running = false;
            options?.onComplete?.();
            return;
          }
        } else {
          currentTime = elapsed % totalDuration;
          progress = totalDuration > 0 ? (elapsed / totalDuration) : 1;

          if (!loop && elapsed >= totalDuration) {
            currentTime = totalDuration;
            progress = 1;
          }
        }
      }

      const seg = findKeyframeSegment(currentTime);
      const segEasing = seg.b.easing || easing.linear;
      const easedT = segEasing(seg.localT);

      const position: [number, number, number] = [
        lerp(seg.a.position[0], seg.b.position[0], easedT),
        lerp(seg.a.position[1], seg.b.position[1], easedT),
        lerp(seg.a.position[2], seg.b.position[2], easedT),
      ];
      const heading = lerpAngle(seg.a.heading, seg.b.heading, easedT);
      const tilt = lerp(seg.a.tilt, seg.b.tilt, easedT);
      const roll = lerp(seg.a.roll ?? 0, seg.b.roll ?? 0, easedT);
      const fov = lerp(seg.a.fov ?? 60, seg.b.fov ?? 60, easedT);

      if (scene && typeof scene.setCamera === 'function') {
        scene.setCamera({ position, heading, tilt, roll, fov });
      }

      if (seg.index !== lastKeyframeIndex) {
        lastKeyframeIndex = seg.index;
        options?.onKeyframe?.(seg.index, sorted[seg.index]);
      }

      const fraction = totalDuration > 0 ? currentTime / totalDuration : 1;
      options?.onProgress?.(currentTime, fraction, seg.index);

      if (!loop && !pingPong && elapsed >= totalDuration && totalDuration > 0) {
        running = false;
        options?.onComplete?.();
        return;
      }

      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);

    return {
      pause() {
        paused = true;
        pauseTime = performance.now();
      },
      resume() {
        if (paused) {
          startTime += performance.now() - pauseTime;
          paused = false;
        }
      },
      stop() {
        running = false;
      },
      isRunning() {
        return running;
      },
      getProgress() {
        return Math.min(progress, 1);
      },
    };
  },

  along3D(
    feature: any,
    path: [number, number, number][],
    options: MIAlong3DOptions
  ): MIAnimationHandle {
    if (!path || path.length < 2) {
      return {
        pause() {},
        resume() {},
        stop() {},
        isRunning() { return false; },
        getProgress() { return 0; },
      };
    }

    const segmentDistances: number[] = [];
    let totalDistance = 0;
    for (let i = 1; i < path.length; i++) {
      const d = haversineDistance(
        path[i - 1][0], path[i - 1][1], path[i - 1][2],
        path[i][0], path[i][1], path[i][2]
      );
      segmentDistances.push(d);
      totalDistance += d;
    }

    const duration = options.speed && options.speed > 0
      ? (totalDistance / options.speed) * 1000
      : options.duration;

    const easingFn = options.easing || easing.linear;
    const orientToPath = options.orientToPath ?? true;
    const tiltToPath = options.tiltToPath ?? false;
    const rollFactor = options.rollFactor ?? 0;
    const altitudeOffset = options.altitude ?? 0;

    let running = true;
    let paused = false;
    let progress = 0;
    let startTime = performance.now();
    let pauseTime = 0;

    function tick(now: number) {
      if (!running) return;
      if (paused) {
        requestAnimationFrame(tick);
        return;
      }

      const elapsed = now - startTime;
      progress = Math.min(elapsed / duration, 1);
      const easedProgress = easingFn(progress);

      const targetDist = easedProgress * totalDistance;
      let accum = 0;
      let position: [number, number, number] = [path[0][0], path[0][1], path[0][2] + altitudeOffset];
      let segIndex = 0;
      let segFraction = 0;

      for (let i = 0; i < segmentDistances.length; i++) {
        if (accum + segmentDistances[i] >= targetDist) {
          segFraction = segmentDistances[i] > 0 ? (targetDist - accum) / segmentDistances[i] : 0;
          segIndex = i;
          position = [
            lerp(path[i][0], path[i + 1][0], segFraction),
            lerp(path[i][1], path[i + 1][1], segFraction),
            lerp(path[i][2], path[i + 1][2], segFraction) + altitudeOffset,
          ];
          break;
        }
        accum += segmentDistances[i];
        if (i === segmentDistances.length - 1) {
          position = [
            path[path.length - 1][0],
            path[path.length - 1][1],
            path[path.length - 1][2] + altitudeOffset,
          ];
          segIndex = i;
          segFraction = 1;
        }
      }

      let heading = 0;
      if (orientToPath && segIndex < path.length - 1) {
        heading = bearingBetween(
          path[segIndex][0], path[segIndex][1],
          path[segIndex + 1][0], path[segIndex + 1][1]
        );
      }

      if (feature) {
        if (typeof feature.setPosition === 'function') {
          feature.setPosition(position);
        } else if (typeof feature.setCoordinates === 'function') {
          feature.setCoordinates(position);
        }

        if (orientToPath && typeof feature.setHeading === 'function') {
          feature.setHeading(heading);
        }

        if (tiltToPath && segIndex < path.length - 1 && typeof feature.setTilt === 'function') {
          const dAlt = path[segIndex + 1][2] - path[segIndex][2];
          const segDist = segmentDistances[segIndex];
          const tiltAngle = segDist > 0 ? toDegrees(Math.atan2(dAlt, segDist)) : 0;
          feature.setTilt(tiltAngle);
        }

        if (rollFactor !== 0 && typeof feature.setRoll === 'function') {
          let headingChange = 0;
          if (segIndex < path.length - 2) {
            const h1 = bearingBetween(path[segIndex][0], path[segIndex][1], path[segIndex + 1][0], path[segIndex + 1][1]);
            const h2 = bearingBetween(path[segIndex + 1][0], path[segIndex + 1][1], path[segIndex + 2][0], path[segIndex + 2][1]);
            headingChange = h2 - h1;
            while (headingChange > 180) headingChange -= 360;
            while (headingChange < -180) headingChange += 360;
          }
          feature.setRoll(headingChange * rollFactor);
        }
      }

      options.onProgress?.(progress, position, heading);

      if (progress >= 1) {
        if (options.repeat) {
          startTime = now;
          requestAnimationFrame(tick);
        } else {
          running = false;
          options.onComplete?.();
        }
      } else {
        requestAnimationFrame(tick);
      }
    }

    requestAnimationFrame(tick);

    return {
      pause() {
        paused = true;
        pauseTime = performance.now();
      },
      resume() {
        if (paused) {
          startTime += performance.now() - pauseTime;
          paused = false;
        }
      },
      stop() {
        running = false;
      },
      isRunning() {
        return running;
      },
      getProgress() {
        return progress;
      },
    };
  },
};
